// Money / date / channel display helpers.
//
// Money is integer cents on the wire (see admin/backend/src/domain/types.ts).
// Frontend formats once at the edge of render.

import type { Channel, Timestamp } from '@goodsweat/domain';

export function formatCents(cents: number, currency = 'usd'): string {
  const value = (cents ?? 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Fallback if currency code unknown.
    return `$${value.toFixed(2)}`;
  }
}

/**
 * Accepts any of the brand's three timestamp encodings (see domain/types.ts).
 * Returns a JS Date or null on bad input.
 */
export function toDate(value: Timestamp | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000 + Math.floor(value.nanoseconds / 1e6));
  }
  return null;
}

export function formatDate(value: Timestamp | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

export function formatDateTime(value: Timestamp | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

const CHANNEL_LABELS: Record<Channel, string> = {
  stripe: 'Stripe',
  tiktok: 'TikTok Shop',
  amazon: 'Amazon',
  manual: 'Manual',
};

export function formatChannel(channel: Channel): string {
  return CHANNEL_LABELS[channel] ?? channel;
}

export function initials(name: string | null | undefined, fallback = 'GS'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || fallback;
}

/**
 * Visually truncate an order id like `cs_test_a1Gk…WSiO` — first 8 chars +
 * single-char ellipsis + last 4 chars. IDs shorter than 16 chars pass through
 * unchanged so we never lose info.
 */
export function truncateOrderId(id: string | null | undefined, leading = 8, trailing = 4): string {
  if (!id) return '—';
  if (id.length <= leading + trailing + 2) return id;
  return `${id.slice(0, leading)}…${id.slice(-trailing)}`;
}

const RELATIVE_DIVISIONS: Array<[number, Intl.RelativeTimeFormatUnit]> = [
  [60, 'seconds'],
  [60, 'minutes'],
  [24, 'hours'],
  [7, 'days'],
  [4.34524, 'weeks'],
  [12, 'months'],
  [Number.POSITIVE_INFINITY, 'years'],
];

const relativeFmt = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

/**
 * "2 hours ago" / "in 3 days" — small helper for activity timestamps.
 * Falls back to `formatDateTime` if the timestamp is bad.
 */
export function formatRelative(value: Timestamp | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  let diff = (d.getTime() - Date.now()) / 1000;
  for (const [amount, unit] of RELATIVE_DIVISIONS) {
    if (Math.abs(diff) < amount) {
      return relativeFmt.format(Math.round(diff), unit);
    }
    diff /= amount;
  }
  return formatDateTime(d);
}

/**
 * Full ISO 8601 string with local timezone, for tooltips on relative
 * timestamps. Returns null for invalid inputs.
 */
export function formatAbsolute(value: Timestamp | null | undefined): string | null {
  const d = toDate(value);
  if (!d) return null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const main = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(d);
  return `${main} (${tz})`;
}

/**
 * Email-masking detector. TikTok forwards customer email through addresses
 * like `abcd1234@orders.tiktok.com`; once we have a Customer doc we also
 * trust `email_address_kind === 'masked'`. This helper accepts either or
 * a plain email string.
 */
export function isMaskedEmail(
  emailOrKind:
    | string
    | { address?: string; kind?: 'direct' | 'masked' | 'unknown' }
    | null
    | undefined,
): boolean {
  if (!emailOrKind) return false;
  if (typeof emailOrKind === 'string') {
    return /@orders\.tiktok\.com$/i.test(emailOrKind);
  }
  if (emailOrKind.kind === 'masked') return true;
  if (emailOrKind.address && /@orders\.tiktok\.com$/i.test(emailOrKind.address)) {
    return true;
  }
  return false;
}
